import { useCallback, useLayoutEffect, useState } from 'react';

const TOOLTIP_ARROW_HALF_WIDTH = 8;
const DEFAULT_MARGIN = 16;

/**
 * Hook to dynamically position a tooltip within the viewport and optionally track arrow position.
 *
 * @param {Object} params
 * @param {boolean} params.contentVisible - Whether the tooltip is currently visible
 * @param {boolean} params.trackArrow - When true, dynamically positions the arrow to point at the
 *   trigger element even when the tooltip has been repositioned
 * @param {React.RefObject} params.tooltipRef - Ref to the tooltip element
 * @param {React.RefObject} params.containerRef - Ref to the container/trigger element
 * @param {Object} [params.clamp] - Configuration for clamping tooltip within viewport
 * @param {boolean} params.clamp.enabled - When true, repositions tooltip to stay within viewport bounds
 * @param {number} [params.clamp.margin=16] - Minimum margin from viewport edges in pixels
 * @param {string} [params.clamp.transform='translate(0, 50px)'] - CSS transform to apply when clamped
 * @returns {Object|null} Style object to apply to the tooltip, or null when not needed
 */
const useTooltipPosition = ({ contentVisible, trackArrow, tooltipRef, containerRef, clamp }) => {
  const [tooltipStyle, setTooltipStyle] = useState(null);

  const clampEnabled = clamp?.enabled;
  const clampMargin = clamp?.margin;
  const clampTransform = clamp?.transform;

  const updatePosition = useCallback(() => {
    const tooltipElement = tooltipRef.current;
    const containerElement = containerRef.current;
    if (!tooltipElement || !containerElement) {
      return;
    }

    const margin = clampMargin ?? DEFAULT_MARGIN;
    const tooltipRect = tooltipElement.getBoundingClientRect();
    const containerRect = containerElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const triggerCenter = containerRect.left + containerRect.width / 2;
    const nextStyle = {};
    let tooltipLeft = tooltipRect.left;

    if (clampEnabled) {
      let left = triggerCenter - tooltipRect.width / 2;
      const minLeft = margin;
      const maxLeft = viewportWidth - tooltipRect.width - margin;

      if (maxLeft < minLeft) {
        left = minLeft;
      } else {
        left = Math.min(Math.max(left, minLeft), maxLeft);
      }

      tooltipLeft = left;
      const leftRelative = left - containerRect.left;
      nextStyle.left = `${leftRelative}px`;
      nextStyle.transform = clampTransform ?? 'translate(0, 50px)';
    }

    if (trackArrow) {
      let arrowLeft = triggerCenter - tooltipLeft - TOOLTIP_ARROW_HALF_WIDTH;
      const arrowMin = TOOLTIP_ARROW_HALF_WIDTH;
      const arrowMax = Math.max(
        TOOLTIP_ARROW_HALF_WIDTH,
        tooltipRect.width - TOOLTIP_ARROW_HALF_WIDTH * 2
      );
      arrowLeft = Math.min(Math.max(arrowLeft, arrowMin), arrowMax);

      nextStyle['--tooltip-arrow-left'] = `${arrowLeft}px`;
    }

    setTooltipStyle(nextStyle);
  }, [clampEnabled, clampMargin, clampTransform, containerRef, tooltipRef, trackArrow]);

  useLayoutEffect(() => {
    if (!contentVisible || (!trackArrow && !clampEnabled)) {
      setTooltipStyle(null);
      return;
    }

    const tooltipElement = tooltipRef.current;
    const containerElement = containerRef.current;
    if (!tooltipElement || !containerElement) {
      return;
    }

    updatePosition();
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
    };
  }, [clampEnabled, contentVisible, trackArrow, updatePosition, tooltipRef, containerRef]);

  return tooltipStyle;
};

export default useTooltipPosition;
